import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
  ReloadOutlined,
  ProfileOutlined,
  SaveOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  ConfigProvider,
  Form,
  Input,
  InputNumber,
  Modal as AntModal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import frFR from "antd/locale/fr_FR";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";

const R_ENDPOINT = "/api/investigation/rubriques";
const P_ENDPOINT = "/api/investigation/points";

const PointsInvestigation = () => {
  const [rubriques, setRubriques] = useState([]);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [intro, setIntro] = useState("");
  const [directeur, setDirecteur] = useState("");
  const [introSaving, setIntroSaving] = useState(false);

  const [rubriqueModal, setRubriqueModal] = useState(false);
  const [pointModal, setPointModal] = useState(false);
  const [editingRubrique, setEditingRubrique] = useState(null);
  const [editingPoint, setEditingPoint] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rForm] = Form.useForm();
  const [pForm] = Form.useForm();

  const fetchAll = () => {
    setLoading(true);
    Promise.all([axiosInstance.get(R_ENDPOINT), axiosInstance.get(P_ENDPOINT), axiosInstance.get("/api/investigation/config")])
      .then(([r, p, c]) => {
        setRubriques(Array.isArray(r.data) ? r.data : []);
        setPoints(Array.isArray(p.data) ? p.data : []);
        setIntro(c?.data?.introTemplate || "");
        setDirecteur(c?.data?.directeurNom || "");
      })
      .catch(() => toast.error("Erreur lors du chargement du catalogue"))
      .finally(() => setLoading(false));
  };

  const saveIntro = () => {
    setIntroSaving(true);
    axiosInstance
      .put("/api/investigation/config", { introTemplate: intro, directeurNom: directeur })
      .then(() => toast.success("Configuration du mandat enregistrée"))
      .catch(() => toast.error("Échec de l'enregistrement de l'introduction"))
      .finally(() => setIntroSaving(false));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const rubriqueLabel = useMemo(() => {
    const map = {};
    rubriques.forEach((r) => (map[r.codeId] = r.intitule || "Vérifications générales"));
    return map;
  }, [rubriques]);

  /* ── Rubriques ── */
  const openRubrique = (r = null) => {
    setEditingRubrique(r);
    rForm.setFieldsValue(
      r
        ? { intitule: r.intitule, ordre: r.ordre, demandeLieu: !!r.demandeLieu, actif: r.actif !== false }
        : { intitule: "", ordre: (rubriques.length + 1), demandeLieu: false, actif: true }
    );
    setRubriqueModal(true);
  };

  const submitRubrique = async (values) => {
    setSaving(true);
    try {
      const payload = { ...values, codeId: editingRubrique?.codeId };
      if (editingRubrique) await axiosInstance.put(R_ENDPOINT, payload);
      else await axiosInstance.post(R_ENDPOINT, payload);
      toast.success(editingRubrique ? "Rubrique modifiée" : "Rubrique ajoutée");
      setRubriqueModal(false);
      fetchAll();
    } catch (_) {
      toast.error("Échec de l'enregistrement de la rubrique");
    } finally {
      setSaving(false);
    }
  };

  const deleteRubrique = (r) => {
    AntModal.confirm({
      title: "Supprimer cette rubrique ?",
      icon: <ExclamationCircleFilled style={{ color: "#fa541c" }} />,
      content: `« ${r.intitule || "Vérifications générales"} » — ses points ne seront plus rattachés.`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () =>
        axiosInstance
          .delete(`${R_ENDPOINT}/${r.codeId}`)
          .then(() => { toast.success("Rubrique supprimée"); fetchAll(); })
          .catch(() => { toast.error("Échec de la suppression"); throw new Error(); }),
    });
  };

  /* ── Points ── */
  const openPoint = (p = null) => {
    setEditingPoint(p);
    pForm.setFieldsValue(
      p
        ? { intitule: p.intitule, rubriqueCodeId: p.rubriqueCodeId, ordre: p.ordre, actif: p.actif !== false }
        : { intitule: "", rubriqueCodeId: rubriques[0]?.codeId, ordre: 1, actif: true }
    );
    setPointModal(true);
  };

  const submitPoint = async (values) => {
    setSaving(true);
    try {
      const payload = { ...values, codeId: editingPoint?.codeId };
      if (editingPoint) await axiosInstance.put(P_ENDPOINT, payload);
      else await axiosInstance.post(P_ENDPOINT, payload);
      toast.success(editingPoint ? "Point modifié" : "Point ajouté");
      setPointModal(false);
      fetchAll();
    } catch (_) {
      toast.error("Échec de l'enregistrement du point");
    } finally {
      setSaving(false);
    }
  };

  const deletePoint = (p) => {
    AntModal.confirm({
      title: "Supprimer ce point ?",
      icon: <ExclamationCircleFilled style={{ color: "#fa541c" }} />,
      content: p.intitule,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () =>
        axiosInstance
          .delete(`${P_ENDPOINT}/${p.codeId}`)
          .then(() => { toast.success("Point supprimé"); fetchAll(); })
          .catch(() => { toast.error("Échec de la suppression"); throw new Error(); }),
    });
  };

  const rubriqueColumns = [
    { title: "Intitulé", dataIndex: "intitule", key: "intitule", render: (v) => v || <i>Vérifications générales</i> },
    { title: "Ordre", dataIndex: "ordre", key: "ordre", width: 90, align: "center" },
    { title: "Lieu ?", dataIndex: "demandeLieu", key: "demandeLieu", width: 110, align: "center", render: (v) => (v ? <Tag color="cyan">Lieu demandé</Tag> : "—") },
    {
      title: "Actions", key: "a", width: 110, align: "center", render: (_, r) => (
        <Space>
          <Tooltip title="Modifier"><Button type="link" icon={<EditOutlined />} onClick={() => openRubrique(r)} /></Tooltip>
          <Tooltip title="Supprimer"><Button type="link" danger icon={<DeleteOutlined />} onClick={() => deleteRubrique(r)} /></Tooltip>
        </Space>
      ),
    },
  ];

  const pointColumns = [
    { title: "Point à vérifier", dataIndex: "intitule", key: "intitule" },
    { title: "Rubrique", dataIndex: "rubriqueCodeId", key: "rubriqueCodeId", width: 260, render: (v) => <Tag>{rubriqueLabel[v] || "—"}</Tag> },
    { title: "Ordre", dataIndex: "ordre", key: "ordre", width: 90, align: "center" },
    {
      title: "Actions", key: "a", width: 110, align: "center", render: (_, p) => (
        <Space>
          <Tooltip title="Modifier"><Button type="link" icon={<EditOutlined />} onClick={() => openPoint(p)} /></Tooltip>
          <Tooltip title="Supprimer"><Button type="link" danger icon={<DeleteOutlined />} onClick={() => deletePoint(p)} /></Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <style>{`
        .inv-header { background: linear-gradient(135deg, #0f766e 0%, #0e7490 100%); border-radius: 16px; padding: 26px 32px; margin-bottom: 24px; color: #fff; box-shadow: 0 4px 20px rgba(15,118,110,0.25); }
        .inv-header h2 { color: #fff; font-size: 24px; font-weight: 700; margin: 0 0 6px 0; }
        .inv-header p { color: rgba(255,255,255,.85); margin: 0; }
      `}</style>
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper" style={{ background: "#f5f5f5" }}>
            <div className="container-xxl flex-grow-1 container-p-y">
              <div className="inv-header">
                <h2><ProfileOutlined style={{ marginRight: 10 }} />Points du Mandat d'Investigation</h2>
                <p>Catalogue paramétrable des rubriques et des points à vérifier par l'investigateur</p>
              </div>

              <ConfigProvider locale={frFR}>
                <Card
                  style={{ borderRadius: 12, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                  title={<Space><ProfileOutlined /> Texte d'introduction & signature du mandat</Space>}
                  extra={<Button type="primary" icon={<SaveOutlined />} loading={introSaving} onClick={saveIntro} style={{ background: "#0f766e" }}>Enregistrer</Button>}
                >
                  <Input.TextArea
                    rows={5}
                    value={intro}
                    onChange={(e) => setIntro(e.target.value)}
                    placeholder="Texte d'introduction du mandat d'investigation…"
                  />
                  <div style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>
                    Variables disponibles :{" "}
                    <Tag>{"{reference}"}</Tag> <Tag>{"{date}"}</Tag> <Tag>{"{lieu}"}</Tag> <Tag>{"{investigateur}"}</Tag>
                    {" "}— elles seront remplacées automatiquement (et affichées en gras) sur le document.
                  </div>
                  <div style={{ marginTop: 16, maxWidth: 420 }}>
                    <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>Nom du Directeur (affiché sous « Le Directeur »)</label>
                    <Input
                      value={directeur}
                      onChange={(e) => setDirecteur(e.target.value)}
                      placeholder="Ex : Koffi G. WARBUTIN"
                    />
                  </div>
                </Card>

                <Card
                  style={{ borderRadius: 12, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                  title={<Space><UnorderedListOutlined /> Rubriques</Space>}
                  extra={
                    <Space>
                      <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>Actualiser</Button>
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => openRubrique(null)} style={{ background: "#0f766e" }}>Ajouter une rubrique</Button>
                    </Space>
                  }
                >
                  <Table loading={loading} columns={rubriqueColumns} dataSource={rubriques} rowKey="codeId" pagination={false} size="small" />
                </Card>

                <Card
                  style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                  title={<Space><ProfileOutlined /> Points à vérifier</Space>}
                  extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openPoint(null)} disabled={rubriques.length === 0} style={{ background: "#0f766e" }}>Ajouter un point</Button>}
                >
                  <Table loading={loading} columns={pointColumns} dataSource={points} rowKey="codeId" pagination={{ pageSize: 15, showSizeChanger: true }} size="small" />
                </Card>
              </ConfigProvider>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      {/* Modal rubrique */}
      <AntModal
        title={editingRubrique ? "Modifier la rubrique" : "Ajouter une rubrique"}
        open={rubriqueModal}
        onCancel={() => setRubriqueModal(false)}
        onOk={() => rForm.submit()}
        confirmLoading={saving}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={rForm} layout="vertical" onFinish={submitRubrique} requiredMark="optional">
          <Form.Item label="Intitulé de la rubrique (en-tête du mandat)" name="intitule" extra="Laisser vide pour un bloc sans en-tête (points généraux).">
            <Input placeholder="Ex : À la Gendarmerie de, Au Parquet…" size="large" />
          </Form.Item>
          <Form.Item label="Ordre" name="ordre"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
          <Form.Item label="Demander un lieu à la création du mandat ?" name="demandeLieu" valuePropName="checked" extra="Ex. « À la Gendarmerie de … » → on saisit « Pessidé ».">
            <Switch checkedChildren="Oui" unCheckedChildren="Non" />
          </Form.Item>
        </Form>
      </AntModal>

      {/* Modal point */}
      <AntModal
        title={editingPoint ? "Modifier le point" : "Ajouter un point"}
        open={pointModal}
        onCancel={() => setPointModal(false)}
        onOk={() => pForm.submit()}
        confirmLoading={saving}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={pForm} layout="vertical" onFinish={submitPoint} requiredMark="optional">
          <Form.Item label="Point à vérifier" name="intitule" rules={[{ required: true, message: "Intitulé requis" }]}>
            <Input.TextArea rows={2} placeholder="Ex : La matérialité des faits (la date, l'heure, l'état des lieux)" />
          </Form.Item>
          <Form.Item label="Rubrique" name="rubriqueCodeId" rules={[{ required: true, message: "Rubrique requise" }]}>
            <Select
              size="large"
              options={rubriques.map((r) => ({ value: r.codeId, label: r.intitule || "Vérifications générales" }))}
              placeholder="Sélectionner la rubrique"
            />
          </Form.Item>
          <Form.Item label="Ordre" name="ordre"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
        </Form>
      </AntModal>
    </div>
  );
};

export default PointsInvestigation;
